import { Download } from 'lucide-react'
import type { ArtifactResponse } from '@/client/types.gen'
import { ArtifactStatusBadge } from '@/components/project/ArtifactStatusBadge'
import { getLabelDisplay } from '@/components/project/artifact-labels'
import {
	formatArtifactDate,
	formatArtifactDisplayFilename,
	formatFileSize,
} from '@/components/project/artifact-viewer/utils/format'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useProcessingPipelinePhase } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'

export interface ArtifactMetadataBlockProps {
	artifact: ArtifactResponse
	onDownload?: () => Promise<void>
	downloading?: boolean
}

export function ArtifactMetadataBlock({
	artifact,
	onDownload,
	downloading,
}: ArtifactMetadataBlockProps) {
	const { t } = useTranslation()
	const processingPipelinePhase = useProcessingPipelinePhase(artifact.id)
	const displayType = `${artifact.extension}${artifact.mime_type ? ` (${artifact.mime_type})` : ''}`

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<p className="text-sm font-medium text-muted-foreground">
					{t('pages.projectDetail.artifactPreview.status')}
				</p>
				<ArtifactStatusBadge
					status={artifact.status}
					processingPipelinePhase={processingPipelinePhase}
					showSpinner
				/>
			</div>

			<div className="space-y-2">
				<p className="text-sm font-medium text-muted-foreground">
					{t('pages.projectDetail.artifactPreview.type')}
				</p>
				<p className="text-sm text-foreground">{displayType || '—'}</p>
			</div>

			<div className="space-y-2">
				<p className="text-sm font-medium text-muted-foreground">
					{t('pages.projectDetail.artifactPreview.filename')}
				</p>
				<p className="text-sm text-foreground break-all">
					{formatArtifactDisplayFilename(artifact.name, artifact.extension)}
				</p>
			</div>

			<div className="space-y-2">
				<p className="text-sm font-medium text-muted-foreground">
					{t('pages.projectDetail.artifactPreview.size')}
				</p>
				<p className="text-sm text-foreground">{formatFileSize(artifact.file_size)}</p>
			</div>

			<div className="space-y-2">
				<p className="text-sm font-medium text-muted-foreground">
					{t('pages.projectDetail.artifactPreview.updated')}
				</p>
				<p className="text-sm text-foreground">{formatArtifactDate(artifact.updated_at) || '—'}</p>
			</div>

			<div className="space-y-2">
				<p className="text-sm font-medium text-muted-foreground">
					{t('pages.projectDetail.artifactPreview.timeDimension')}
				</p>
				<p className="text-sm text-foreground">{getLabelDisplay(artifact.time_dimension, t)}</p>
			</div>

			<div className="space-y-2">
				<p className="text-sm font-medium text-muted-foreground">
					{t('pages.projectDetail.artifactPreview.purpose')}
				</p>
				<p className="text-sm text-foreground">{getLabelDisplay(artifact.purpose, t)}</p>
			</div>

			{artifact.purpose === 'other_custom' && (artifact.purpose_custom ?? '').trim() && (
				<div className="space-y-2">
					<p className="text-sm font-medium text-muted-foreground">
						{t('pages.projectDetail.artifactPreview.purposeCustom')}
					</p>
					<p className="text-sm text-foreground">{artifact.purpose_custom}</p>
				</div>
			)}

			{onDownload && (
				<div className="space-y-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={onDownload}
						disabled={downloading || artifact.status === 'pending_upload'}
						aria-label={t('pages.projectDetail.artifactPreview.downloadRawFile')}
					>
						{downloading ? (
							<Spinner className="size-4 shrink-0 mr-2 animate-spin" aria-hidden />
						) : (
							<Download className="size-4 shrink-0 mr-2" aria-hidden />
						)}
						{t('pages.projectDetail.artifactPreview.downloadRawFile')}
					</Button>
				</div>
			)}
		</div>
	)
}
